import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './AdminHealth.css';
import { useLanguage } from '../../context/LanguageContext';

const healthCopy = {
  en: { title:'System health', refresh:'Refresh', profile:'My profile', dashboard:'Dashboard', keys:'API keys and secrets', valid:'All keys are configured', invalid:'One or more keys are missing or invalid', name:'Name', configured:'Configured', yes:'Yes', no:'No', llm:'LLM usage', images:'Image-generation usage', today:'Today', week:'Last 7 days', all:'All time', calls:'calls', imageCount:'images', loading:'Loading…', unreachable:'Could not reach the backend. Check that it is running.', adminOnly:'Doctor/admin access is required for this page. Please sign in with a doctor account.' },
  ru: { title:'Состояние системы', refresh:'Обновить', profile:'Мой профиль', dashboard:'Панель', keys:'Ключи API и секреты', valid:'Все ключи настроены', invalid:'Один или несколько ключей отсутствуют или недействительны', name:'Название', configured:'Настроен', yes:'Да', no:'Нет', llm:'Использование языковой модели', images:'Использование генерации изображений', today:'Сегодня', week:'Последние 7 дней', all:'За всё время', calls:'запросов', imageCount:'изображений', loading:'Загрузка…', unreachable:'Не удалось подключиться к серверу. Проверьте, запущен ли он.', adminOnly:'Для этой страницы нужен доступ врача/администратора. Войдите через аккаунт врача.' },
  uz: { title:'Tizim holati', refresh:'Yangilash', profile:'Mening profilim', dashboard:'Boshqaruv paneli', keys:'API kalitlari va maxfiy ma’lumotlar', valid:'Barcha kalitlar sozlangan', invalid:'Bir yoki bir nechta kalit yo‘q yoki yaroqsiz', name:'Nomi', configured:'Sozlangan', yes:'Ha', no:'Yo‘q', llm:'Til modelidan foydalanish', images:'Tasvir yaratishdan foydalanish', today:'Bugun', week:'So‘nggi 7 kun', all:'Barcha vaqt', calls:'ta so‘rov', imageCount:'ta tasvir', loading:'Yuklanmoqda…', unreachable:'Server bilan bog‘lanib bo‘lmadi. U ishga tushirilganini tekshiring.', adminOnly:'Bu sahifa uchun shifokor/admin ruxsati kerak. Shifokor akkaunti bilan kiring.' }
};

const BACKEND_API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8082/api';

const AdminHealth = () => {
  const { language } = useLanguage();
  const c = healthCopy[language] || healthCopy.en;
  const navigate = useNavigate();
  const [keys, setKeys] = useState(null);
  const [usage, setUsage] = useState(null);
  const [imageUsage, setImageUsage] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      if (!token) {
        throw new Error(c.adminOnly);
      }
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const pingRes = await fetch(`${BACKEND_API_URL}/health`);
      if (!pingRes.ok) {
        throw new Error(`${c.unreachable} (${BACKEND_API_URL})`);
      }
      const [keysRes, usageRes, imageUsageRes] = await Promise.all([
        fetch(`${BACKEND_API_URL}/health/keys`, { headers }),
        fetch(`${BACKEND_API_URL}/usage/llm`, { headers }),
        fetch(`${BACKEND_API_URL}/usage/images`, { headers })
      ]);
      if ([keysRes, usageRes, imageUsageRes].some(res => res.status === 401 || res.status === 403)) {
        throw new Error(c.adminOnly);
      }
      if (!keysRes.ok || !usageRes.ok || !imageUsageRes.ok) {
        throw new Error('Health check failed');
      }
      setKeys(await keysRes.json());
      setUsage(await usageRes.json());
      setImageUsage(await imageUsageRes.json());
    } catch (e) {
      setError(e.message || `${c.unreachable} (${BACKEND_API_URL})`);
    } finally {
      setLoading(false);
    }
  }, [c.adminOnly, c.unreachable]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [load]);

  if (loading) return <div className="admin-health-page"><p>{c.loading}</p></div>;

  return (
    <div className="admin-health-page">
      <h1>{c.title}</h1>
      <div className="health-actions"><button className="refresh-btn" onClick={load}>{c.refresh}</button><button className="refresh-btn" onClick={() => navigate('/profile')}>{c.profile}</button><button className="refresh-btn" onClick={() => navigate('/dashboard')}>{c.dashboard}</button></div>

      {error && <div className="health-error">{error}</div>}

      {keys && (
        <section>
          <h2>{c.keys}</h2>
          <div className={`status-banner ${keys.all_valid ? 'ok' : 'bad'}`}>
            {keys.all_valid ? c.valid : c.invalid}
          </div>
          <table>
            <thead>
              <tr><th>{c.name}</th><th>{c.configured}</th></tr>
            </thead>
            <tbody>
              {keys.checks.map((c) => (
                <tr key={c.name} className={c.present ? '' : 'row-bad'}>
                  <td>{c.name}</td>
                  <td>{c.present ? healthCopy[language]?.yes || healthCopy.en.yes : healthCopy[language]?.no || healthCopy.en.no}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {usage && (
        <section>
          <h2>{c.llm}</h2>
          <div className="usage-cards">
            <div className="usage-card">
              <span className="usage-label">{c.today}</span>
              <span className="usage-value">${usage.today?.total_cost_usd?.toFixed(4)}</span>
              <span className="usage-sub">{usage.today?.calls ?? 0} {c.calls}</span>
            </div>
            <div className="usage-card">
              <span className="usage-label">{c.week}</span>
              <span className="usage-value">${usage.last_7_days?.total_cost_usd?.toFixed(4)}</span>
              <span className="usage-sub">{usage.last_7_days?.calls ?? 0} {c.calls}</span>
            </div>
            <div className="usage-card">
              <span className="usage-label">{c.all}</span>
              <span className="usage-value">${usage.all_time?.total_cost_usd?.toFixed(4)}</span>
              <span className="usage-sub">{usage.all_time?.calls ?? 0} {c.calls}</span>
            </div>
          </div>
        </section>
      )}

      {imageUsage && (
        <section>
          <h2>{c.images}</h2>
          <div className="usage-cards">
            <div className="usage-card">
              <span className="usage-label">{c.today}</span>
              <span className="usage-value">${imageUsage.today?.cost_usd?.toFixed(4)}</span>
              <span className="usage-sub">{imageUsage.today?.images ?? 0} {c.imageCount}</span>
            </div>
            <div className="usage-card">
              <span className="usage-label">{c.all}</span>
              <span className="usage-value">${imageUsage.all_time?.cost_usd?.toFixed(4)}</span>
              <span className="usage-sub">{imageUsage.all_time?.images ?? 0} {c.imageCount}</span>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default AdminHealth;
